import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/auth/useProfile';
import { toast } from 'react-hot-toast';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

interface Message {
  id: string;
  content: string;
  user_id: string;
  created_at: string;
  user_profiles: {
    first_name: string;
    last_name: string;
    avatar_url: string | null;
    is_admin: boolean;
  };
}

interface Conversation {
  conversation_id: string;
  other_user_profile: {
    first_name: string;
    last_name: string;
    avatar_url: string | null;
    is_admin: boolean;
  };
  latest_message: {
    content: string;
    created_at: string;
    read: boolean;
  };
  unread_count: number;
}

export function CareerChatPage() {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { profile } = useProfile();

  useEffect(() => {
    loadConversations();
    const channel = supabase
      .channel('chat')
      .on('postgres_changes', { 
        event: 'INSERT', 
        schema: 'public', 
        table: 'chat_messages',
        filter: selectedConversation 
          ? `conversation_id=eq.${selectedConversation}`
          : undefined
      }, payload => {
        if (payload.new.conversation_id === selectedConversation) {
          loadMessages(selectedConversation);
        } else {
          loadConversations();
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedConversation]);

  const loadConversations = async () => {
    try {
      const { data, error } = await supabase.rpc('get_conversations_with_messages');
      if (error) throw error;
      setConversations(data || []);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading conversations:', error);
      toast.error('Failed to load conversations');
    }
  };

  const loadMessages = async (conversationId: string) => {
    try {
      const { data, error } = await supabase
        .from('chat_messages')
        .select(`
          id,
          content,
          user_id,
          created_at,
          user_profiles (
            first_name,
            last_name,
            avatar_url,
            is_admin
          )
        `)
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMessages(data || []);
      scrollToBottom();

      // Mark messages as read
      await supabase
        .from('chat_messages')
        .update({ read: true })
        .eq('conversation_id', conversationId)
        .eq('user_id', user?.id)
        .eq('read', false);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load messages');
    }
  };

  const startConversation = async () => {
    try {
      // Find an admin to chat with
      const { data: admins, error: adminError } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('is_admin', true)
        .limit(1);

      if (adminError) throw adminError;
      if (!admins?.length) {
        toast.error('No admins available for chat');
        return;
      }

      const { data, error } = await supabase.rpc('start_conversation', {
        other_user_id: admins[0].id
      });

      if (error) throw error;
      setSelectedConversation(data);
      await loadMessages(data);
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error('Failed to start conversation');
    }
  };

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedConversation || !newMessage.trim() || isSending) return;

    setIsSending(true);
    try {
      const { error } = await supabase
        .from('chat_messages')
        .insert({
          conversation_id: selectedConversation,
          user_id: user?.id,
          content: newMessage.trim()
        });

      if (error) throw error;
      setNewMessage('');
      await loadMessages(selectedConversation);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#ddd9d9] py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="mb-6">
          <Link
            to="/prestige-club"
            className="inline-flex items-center text-[#800020] hover:opacity-80"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to My Dashboard
          </Link>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 h-[calc(100vh-12rem)] relative">
            {/* Conversations List */}
            <div className="border-r border-gray-200 md:block">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-black">Career Coaching</h2>
                {conversations.length === 0 && (
                  <button
                    onClick={startConversation}
                    className="mt-4 w-full px-4 py-2 bg-[#800020] text-white rounded-lg hover:opacity-90 transition-colors"
                  >
                    Start Conversation
                  </button>
                )}
              </div>
              <div className="overflow-y-auto h-[calc(100vh-16rem)]">
                {conversations.map((conversation) => (
                  <button
                    key={conversation.conversation_id}
                    onClick={() => {
                      setSelectedConversation(conversation.conversation_id);
                      loadMessages(conversation.conversation_id);
                    }}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      selectedConversation === conversation.conversation_id ? 'bg-gray-50' : ''
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <img
                        src={conversation.other_user_profile.avatar_url || `https://ui.avatars.com/api/?name=${encodeURIComponent(
                          `${conversation.other_user_profile.first_name} ${conversation.other_user_profile.last_name}`
                        )}`}
                        alt="Profile"
                        className="w-10 h-10 rounded-full"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-black truncate">
                            {conversation.other_user_profile.first_name} {conversation.other_user_profile.last_name}
                          </p>
                          {conversation.unread_count > 0 && (
                            <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-[#800020] rounded-full">
                              {conversation.unread_count}
                            </span>
                          )}
                        </div>
                        {conversation.latest_message && (
                          <p className="text-sm text-gray-500 truncate">
                            {conversation.latest_message.content}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Chat Area */}
            <div className="col-span-2 flex flex-col h-full">
              {selectedConversation ? (
                <>
                  {/* Messages */}
                  <div className="flex-1 p-4 overflow-y-auto min-h-0 pb-20 md:pb-4">
                    <div className="space-y-4">
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${
                            message.user_id === user?.id ? 'justify-end' : 'justify-start'
                          }`}
                        >
                          <div
                            className={`max-w-[70%] rounded-lg p-3 ${
                              message.user_id === user?.id
                                ? 'bg-[#800020] text-white'
                                : 'bg-gray-100 text-black'
                            }`}
                          >
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-medium">
                                {message.user_profiles.first_name} {message.user_profiles.last_name}
                              </span>
                              {message.user_profiles.is_admin && (
                                <span className="px-2 py-0.5 bg-black/10 rounded-full text-xs">
                                  Admin
                                </span>
                              )}
                            </div>
                            <p className="text-sm">{message.content}</p>
                            <span className="text-xs opacity-70">
                              {new Date(message.created_at).toLocaleTimeString()}
                            </span>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </div>

                  {/* Message Input */}
                  <form onSubmit={sendMessage} className="p-4 border-t border-gray-200 bg-white fixed bottom-0 left-0 right-0 md:relative">
                    <div className="flex space-x-4">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1 px-4 py-2 bg-white text-black border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#800020] focus:border-[#800020] w-full"
                      />
                      <button
                        type="submit"
                        disabled={!newMessage.trim() || isSending}
                        className="px-4 py-2 bg-[#800020] text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 flex items-center shrink-0"
                      >
                        {isSending ? (
                          <LoadingSpinner size="sm" />
                        ) : (
                          <Send className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </form>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <h3 className="text-lg font-medium text-black mb-2">
                      Select a conversation
                    </h3>
                    <p className="text-gray-500">
                      Choose a conversation from the list to start chatting
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}